<script setup>
import DatePicker from 'primevue/datepicker';
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: [String, Date],
        default: ''
    },
    defaultValue: {
        type: [String, Date],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    width: {
        type: String,
        default: '12.5rem'
    },
    errmsg: {
        type: String,
        default: ''
    },
    readonly: {
        type: Boolean,
        default: false
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    padding: {
        type: String,
        default: ''
    },
    height: {
        type: String,
        default: '2.3rem'
    }
});

const emit = defineEmits(['update:modelValue', 'date-select', 'input', 'change']);
const isReadonly = computed(() => props.readonly);

// formatDate 함수: Date 객체를 "YYYY" 문자열로 변환
const formatDate = (date) => {
    if (!date || isNaN(new Date(date))) return null;
    return new Date(date).getFullYear().toString();
};

// parseDate 함수: "YYYY" 문자열을 Date 객체로 변환
const parseDate = (value) => {
    if (!value || value.length !== 4) return null;
    const year = parseInt(value, 10);
    return !isNaN(year) && year >= 1000 && year <= 9999 ? new Date(year, 0, 1) : null;
};

// 초기값 설정: props.modelValue를 Date 객체로 변환하여 사용
const dateValue = ref(parseDate(props.modelValue) || null);

const datePickerRef = ref(null);
const errMessage = ref(props.errmsg);

// modelValue 감시: 부모 컴포넌트에서 modelValue가 변경될 때
watch(
    () => props.modelValue,
    (newValue) => {
        dateValue.value = parseDate(newValue); // Date 객체로 변환하여 할당.
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        errMessage.value = newVal || '';
    },
    { immediate: true }
);

// onMounted: defaultValue가 있으면 설정
onMounted(() => {
    if (props.defaultValue) {
        const initialDate = parseDate(props.defaultValue);
        dateValue.value = initialDate; // Date 객체로
        emit('update:modelValue', formatDate(initialDate)); // 문자열로
    }
});

const defaultPlaceholder = computed(() => {
    return props.placeholder || 'ex) ' + new Date().getFullYear();
});

// onDateSelect 핸들러: DatePicker에서 날짜를 선택했을 때
const onDateSelect = (event) => {
    if (event) {
        const formatted = formatDate(event);
        emit('update:modelValue', formatted); // YYYY 문자열로 변경
        emit('input', formatted);
        errMessage.value = '';
        datePickerRef.value.overlayVisible = false; // DatePicker 닫기
    }
};

// onInput 핸들러: 사용자가 입력 필드에 값을 입력했을 때
const onInput = (event) => {
    errMessage.value = '';
    let value = event.target.value.replace(/[^0-9]/g, '').substring(0, 4);

    const parsedDate = parseDate(value);
    dateValue.value = parsedDate;

    if (parsedDate) {
        emit('update:modelValue', formatDate(parsedDate));
        emit('input', formatDate(parsedDate));
        errMessage.value = '';
        datePickerRef.value.overlayVisible = false; // DatePicker 닫기
    } else {
        errMessage.value = value.length === 4 ? '유효하지 않은 연도입니다.' : '';
        emit('update:modelValue', value);
        emit('input', value);
    }
};

const onBlur = () => {
    if (dateValue.value && formatDate(dateValue.value).length === 4) {
        errMessage.value = '';
    } else {
        errMessage.value = '유효하지 않은 날짜형식입니다.';
    }
};

const openDatePicker = () => {
    if (!props.readonly) {
        datePickerRef.value.overlayVisible = true;
    }
};

const onTodayClick = () => {
    const today = new Date();
    dateValue.value = today;
    emit('update:modelValue', formatDate(today));
    emit('input', formatDate(today));
    datePickerRef.value.overlayVisible = false;
};

const onClearClick = () => {
    errMessage.value = '';
    dateValue.value = null;
    emit('update:modelValue', '');
    emit('input', '');
    datePickerRef.value.overlayVisible = false;
};
</script>

<template>
    <div
        v-if="visible"
        class="flex flex-col w-full"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="errMessage" class="validation-message ml-1">{{ errMessage }}</span>
        </label>
        <div class="relative w-full flex items-center h-12">
            <DatePicker
                ref="datePickerRef"
                v-model="dateValue"
                :inputId="inputId"
                :placeholder="defaultPlaceholder"
                :readonly="isReadonly"
                :disabled="isReadonly"
                @date-select="onDateSelect"
                @input="onInput"
                class="h-[2.5rem]"
                :class="['custom-datepicker w-full', { 'error-border': errMessage }]"
                view="year"
                dateFormat="yy"
                :manualInput="true"
                :showOnFocus="false"
                :style="{ textAlign: 'center', width: '100%' }"
            >
                <template #footer>
                    <div class="p-datepicker-buttonbar">
                        <button type="button" class="p-button-text custom-today-button" @click="onTodayClick">오늘</button>
                        <button type="button" class="p-button-text custom-clear-button" @click="onClearClick">초기화</button>
                    </div>
                </template>
            </DatePicker>
            <button v-if="dateValue && !isReadonly" @click="onClearClick" class="clear-button">&times;</button>
            <span v-if="!isReadonly" class="date-icon pi pi-calendar" @click="openDatePicker"></span>
        </div>
    </div>
</template>

<style scoped>
.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
    user-select: none;
}

.custom-datepicker :deep(.p-inputtext) {
    /* padding: 0.75rem; 기본 높이 */
    border: 1px solid #d1d5db; /* 기본 테두리 색상 */
    border-radius: 0.5rem; /* 기본 border-radius */
    font-size: 0.9rem;
    width: 100% !important;
}

.custom-datepicker.readonly-bg :deep(.p-inputtext) {
    background-color: whitesmoke !important;
}

.readonly-Bg {
    background-color: whitesmoke !important;
}

.custom-datepicker.error-border :deep(.p-inputtext) {
    border-color: #f87171 !important; /* 빨간색 테두리 */
}

.custom-datepicker :deep(.p-inputtext:focus) {
    border-color: #0038ea !important;
    outline: none;
}

.custom-datepicker :deep(.p-inputtext[readonly]:focus) {
    border-color: #d1d5db !important;
    outline: none;
}

.custom-datepicker :deep(.p-inputtext:disabled) {
    background-color: whitesmoke;
    opacity: 1;
    cursor: default;
}

.clear-button {
    position: absolute;
    right: 2.5rem;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    width: 1rem;
    height: 1rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    background: transparent;
    border: none;
    opacity: 0.5;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
}

.clear-button:hover {
    font-family: 'SCoreDream500';
    opacity: 1;
}

.date-icon {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1rem;
    color: #6b7280;
    cursor: pointer;
}

.custom-today-button {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    margin-left: 0.5rem;
    color: black !important;
}

.custom-clear-button {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    margin-right: 0.5rem;
    color: black !important;
}

.validation-message {
    color: #f87171; /* red-500 */
    font-size: 0.75rem;
    margin-left: 0.5rem;
}
</style>
